import countries from "i18n-iso-countries";
import json from "i18n-iso-countries/langs/en.json";

import { CountryISOCode } from "@vega-api/generated/types/countryISOCode";
import { type StateISOCode } from "@vega-api/generated/types/stateISOCode";
import { m } from "@vega-core/paraglide/messages";

/**
 * Custom hook to get translated country and state names using i18n-iso-countries.
 * @returns An object containing functions to get translated country and state names.
 */
export const useLocationTranslations = () => {
  const isUSA = (code?: CountryISOCode) => code === CountryISOCode.US;

  countries.registerLocale(json);

  /**
   * Gets the translated country name for a given ISO code.
   * @param code - The CountryISOCode.
   * @returns The translated country name.
   */
  const getTranslatedCountryName = (
    code?: CountryISOCode,
  ): string | undefined =>
    code && countries.getName(code, "en", { select: "official" });

  /**
   * Gets the translated state name for a given ISO code.
   * @param code - The StateISOCode.
   * @returns The translated state name.
   */
  const getTranslatedStateName = (code?: StateISOCode): string | undefined => {
    if (!code) {
      return undefined;
    }
    const translatedStateNames: { [key in StateISOCode]: string } = {
      AL: m.state_alabama(),
      AK: m.state_alaska(),
      AZ: m.state_arizona(),
      AR: m.state_arkansas(),
      CA: m.state_california(),
      CO: m.state_colorado(),
      CT: m.state_connecticut(),
      DE: m.state_delaware(),
      FL: m.state_florida(),
      GA: m.state_georgia(),
      HI: m.state_hawaii(),
      IL: m.state_illinois(),
      IN: m.state_indiana(),
      IA: m.state_iowa(),
      KS: m.state_kansas(),
      KY: m.state_kentucky(),
      LA: m.state_louisiana(),
      ME: m.state_maine(),
      MD: m.state_maryland(),
      MA: m.state_massachusetts(),
      MI: m.state_michigan(),
      MN: m.state_minnesota(),
      MS: m.state_mississippi(),
      MO: m.state_missouri(),
      MT: m.state_montana(),
      NE: m.state_nebraska(),
      NV: m.state_nevada(),
      NH: m.state_newHampshire(),
      NJ: m.state_newJersey(),
      NM: m.state_newMexico(),
      NY: m.state_newYork(),
      NC: m.state_northCarolina(),
      ND: m.state_northDakota(),
      OH: m.state_ohio(),
      OK: m.state_oklahoma(),
      OR: m.state_oregon(),
      PA: m.state_pennsylvania(),
      RI: m.state_rhodeIsland(),
      SC: m.state_southCarolina(),
      SD: m.state_southDakota(),
      TN: m.state_tennessee(),
      TX: m.state_texas(),
      UT: m.state_utah(),
      VT: m.state_vermont(),
      VA: m.state_virginia(),
      WA: m.state_washington(),
      WV: m.state_westVirginia(),
      WI: m.state_wisconsin(),
      WY: m.state_wyoming(),
    };

    return translatedStateNames[code];
  };

  return { getTranslatedCountryName, getTranslatedStateName, isUSA };
};
